import { useCallback, useState } from "react";
import { useHasScope } from "../../../utils/Permission";
import ConfirmModal from "../../global/ConfirmModal";
import Notification from "../../global/Notification";
import SyncAllButton from "../SyncAllButton";

type Props = {
    onForceSyncAll?: () => Promise<string>;
    isSyncing?: boolean;
};

/**
 * Force-sync trigger for a report. Renders only for users with the `write-force-sync` scope,
 */
export default function ForceSyncAllButton({ onForceSyncAll, isSyncing }: Props) {
    const hasForceSyncScope = useHasScope('write-force-sync');
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [isConfirmOpen, setConfirmOpen] = useState(false);

    const dismissError = useCallback(() => setErrorMessage(null), []);

    if (!onForceSyncAll || !hasForceSyncScope) return null;

    const handleConfirm = async () => {
        setConfirmOpen(false);
        setErrorMessage(null);
        const message = await onForceSyncAll();
        if (message) setErrorMessage(message);
    };

    return (
        <>
            <SyncAllButton
                className="force-sync-btn"
                label={isSyncing ? "Syncing…" : "Force Sync All"}
                syncing={isSyncing}
                onClick={() => setConfirmOpen(true)}
            />
            <ConfirmModal
                open={isConfirmOpen}
                title="Force sync all?"
                message="This re-sends all records from the entity to Shopify. It can take a while and can't be stopped once it starts."
                confirmLabel="Force sync"
                onCancel={() => setConfirmOpen(false)}
                onConfirm={handleConfirm}
            />
            <Notification message={errorMessage} type="error" onClose={dismissError} />
        </>
    );
}
